import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { usuarioProfileService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

// Rótulos copiados das telas de cada módulo (GestaoOKRs e as telas antigas de Projetos/Cronogramas, removidas em 27/09).
const STATUS_OKR: Record<string, string> = {
  a_fazer: 'A Fazer', fazendo: 'Fazendo', feito: 'Feito', pausado: 'Pausado', cancelado: 'Cancelado',
};
const STATUS_PROJETO: Record<string, string> = {
  em_edicao: 'Em Edição', ag_aprovacao: 'Ag. Aprovação', em_andamento: 'Em Andamento',
  ag_validacao: 'Ag. Validação', finalizado: 'Finalizado', reprovado: 'Reprovado',
};
const STATUS_ATIVIDADE: Record<string, string> = {
  nao_iniciado: 'Não iniciado', em_processo: 'Em processo', finalizado: 'Finalizado', suspenso: 'Suspenso',
};
const STATUS_DIAGRAMA: Record<string, string> = { rascunho: 'Rascunho', ativo: 'Ativo', arquivado: 'Arquivado' };
const PRIORIDADE: Record<string, string> = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgente: 'Urgente' };

/** Cor do selo pelo "estado" do status: concluído / em andamento / parado / neutro. */
const TOM: Record<string, string> = {
  feito: 'bg-emerald-100 text-emerald-800', finalizado: 'bg-emerald-100 text-emerald-800', ativo: 'bg-emerald-100 text-emerald-800',
  fazendo: 'bg-blue-100 text-blue-800', em_andamento: 'bg-blue-100 text-blue-800', em_processo: 'bg-blue-100 text-blue-800',
  ag_aprovacao: 'bg-amber-100 text-amber-800', ag_validacao: 'bg-amber-100 text-amber-800', pausado: 'bg-amber-100 text-amber-800',
  cancelado: 'bg-rose-100 text-rose-800', reprovado: 'bg-rose-100 text-rose-800', suspenso: 'bg-rose-100 text-rose-800',
};

const dataCurta = (v: string | null | undefined) => {
  if (!v) return null;
  const d = new Date(v.length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

const atrasado = (fim: string | null | undefined, status: string) => {
  if (!fim || ['feito', 'finalizado', 'cancelado', 'reprovado'].includes(status)) return false;
  const d = new Date(fim.length === 10 ? `${fim}T23:59:59` : fim);
  return d.getTime() < Date.now();
};

function Selo({ status, rotulos }: { status: string | null | undefined; rotulos: Record<string, string> }) {
  if (!status) return null;
  return <Badge variant="secondary" className={`whitespace-nowrap ${TOM[status] ?? ''}`}>{rotulos[status] ?? status}</Badge>;
}

function Prazo({ inicio, fim, status }: { inicio?: string | null; fim?: string | null; status: string }) {
  const i = dataCurta(inicio);
  const f = dataCurta(fim);
  if (!i && !f) return <span className="text-xs text-muted-foreground">Sem prazo</span>;
  return (
    <span className={`text-xs ${atrasado(fim, status) ? 'font-medium text-rose-600' : 'text-muted-foreground'}`}>
      {i && f ? `${i} → ${f}` : f ? `Até ${f}` : `Desde ${i}`}
      {atrasado(fim, status) ? ' · atrasado' : ''}
    </span>
  );
}

function CartaoItem({ titulo, href, selo, children, onNavigate }: {
  titulo: string; href?: string; selo?: ReactNode; children?: ReactNode; onNavigate?: () => void;
}) {
  return (
    <div className="flex h-full flex-col gap-2 rounded-lg border bg-card p-3 text-card-foreground shadow-sm">
      <div className="flex items-start justify-between gap-2">
        {href ? (
          <Link to={href} onClick={onNavigate} className="group flex min-w-0 items-center gap-1 font-medium hover:underline">
            <span className="truncate" title={titulo}>{titulo}</span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
          </Link>
        ) : (
          <span className="truncate font-medium" title={titulo}>{titulo}</span>
        )}
        {selo}
      </div>
      {children}
    </div>
  );
}

// ------- formatos devolvidos pelo backend -------
interface OkrItem {
  id: string; titulo: string; status: string; progresso: number; data_inicio: string; data_fim: string; papel: string;
  empresa_id: string;
  key_results: { id: string; descricao: string; percentual: number; prazo: string | null; da_pessoa: boolean }[];
}
interface ProjetosItens {
  projetos: { id: string; titulo: string; status_projeto: string | null; data_inicio: string | null; data_fim: string | null; papel: string }[];
  atividades: {
    id: string; titulo: string; status: string; prioridade: string; data_inicio: string | null; data_termino: string | null;
    cronograma_id: string; cronograma_titulo: string;
  }[];
}
interface DiagramaItem { id: string; nome: string; status: string; atualizado_em: string; papel: string }
interface TreinamentoItem { id: string; titulo: string; total_aulas: number; aulas_concluidas: number; progresso: number }
interface FormularioItem {
  id: string; formulario_id: string | null; titulo: string; respondido_em: string;
  respostas: { pergunta: string; tipo: string; valor: unknown }[];
}

const GRADE = 'grid gap-3 md:grid-cols-2 xl:grid-cols-3';

function Vazio({ texto }: { texto: string }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{texto}</p>;
}

function ListaOkrs({ itens }: { itens: OkrItem[] }) {
  const { user, setEmpresaFiltroId } = useAuth();
  if (itens.length === 0) return <Vazio texto="Nenhum OKR ou key result com esta pessoa como responsável." />;
  return (
    <div className={GRADE}>
      {itens.map((o) => (
        <CartaoItem
          key={o.id}
          titulo={o.titulo}
          href={`/okrs?okr=${o.id}`}
          onNavigate={() => { if (user?.tipo === 'superadmin' && o.empresa_id) setEmpresaFiltroId(o.empresa_id); }}
          selo={<Selo status={o.status} rotulos={STATUS_OKR} />}
        >
          <div className="flex items-center gap-2">
            <Progress value={o.progresso} className="h-2 flex-1" />
            <span className="w-10 text-right text-xs font-medium">{Math.round(o.progresso)}%</span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-1">
            <Prazo inicio={o.data_inicio} fim={o.data_fim} status={o.status} />
            <span className="text-xs text-muted-foreground">{o.papel}</span>
          </div>
          {o.key_results.length > 0 && (
            <ul className="space-y-1 border-t pt-2">
              {o.key_results.map((k) => (
                <li key={k.id} className={`flex items-center gap-2 text-xs ${k.da_pessoa ? '' : 'text-muted-foreground'}`}>
                  <span className="min-w-0 flex-1 truncate" title={k.descricao}>
                    {k.da_pessoa && <span className="mr-1 font-semibold text-primary" title="Responsável">●</span>}
                    {k.descricao}
                  </span>
                  <span className="w-9 shrink-0 text-right">{Math.round(k.percentual)}%</span>
                </li>
              ))}
            </ul>
          )}
        </CartaoItem>
      ))}
    </div>
  );
}

function ListaProjetos({ itens }: { itens: ProjetosItens }) {
  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <h4 className="text-sm font-medium">Atividades sob responsabilidade ({itens.atividades.length})</h4>
        {itens.atividades.length === 0 ? (
          <Vazio texto="Nenhuma atividade encontrada. O responsável de atividade é ligado pelo e-mail — confira se o cadastro de responsáveis do projeto usa o mesmo e-mail desta pessoa." />
        ) : (
          <div className={GRADE}>
            {itens.atividades.map((a) => (
              <CartaoItem key={a.id} titulo={a.titulo} href={`/projetos/${a.cronograma_id}`} selo={<Selo status={a.status} rotulos={STATUS_ATIVIDADE} />}>
                <span className="truncate text-xs text-muted-foreground" title={a.cronograma_titulo}>{a.cronograma_titulo}</span>
                <div className="flex flex-wrap items-center justify-between gap-1">
                  <Prazo inicio={a.data_inicio} fim={a.data_termino} status={a.status} />
                  <span className="text-xs text-muted-foreground">Prioridade {PRIORIDADE[a.prioridade]?.toLowerCase() ?? a.prioridade}</span>
                </div>
              </CartaoItem>
            ))}
          </div>
        )}
      </section>
      <section className="space-y-2">
        <h4 className="text-sm font-medium">Projetos que lidera ou criou ({itens.projetos.length})</h4>
        {itens.projetos.length === 0 ? (
          <Vazio texto="Nenhum projeto liderado ou criado por esta pessoa." />
        ) : (
          <div className={GRADE}>
            {itens.projetos.map((c) => (
              <CartaoItem
                key={c.id}
                titulo={c.titulo}
                href={`/projetos/${c.id}`}
                selo={<Selo status={c.status_projeto} rotulos={STATUS_PROJETO} />}
              >
                <div className="flex flex-wrap items-center justify-between gap-1">
                  <Prazo inicio={c.data_inicio} fim={c.data_fim} status={c.status_projeto ?? ''} />
                  <span className="text-xs text-muted-foreground">{c.papel}</span>
                </div>
              </CartaoItem>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ListaDiagramas({ itens, base, nome }: { itens: DiagramaItem[]; base: string; nome: string }) {
  if (itens.length === 0) return <Vazio texto={`Nenhum ${nome} criado ou editado por esta pessoa.`} />;
  return (
    <div className={GRADE}>
      {itens.map((d) => (
        <CartaoItem key={d.id} titulo={d.nome} href={`${base}/${d.id}`} selo={<Selo status={d.status} rotulos={STATUS_DIAGRAMA} />}>
          <div className="flex flex-wrap items-center justify-between gap-1 text-xs text-muted-foreground">
            <span>Atualizado em {dataCurta(d.atualizado_em) ?? '—'}</span>
            <span>{d.papel}</span>
          </div>
        </CartaoItem>
      ))}
    </div>
  );
}

function ListaTreinamentos({ itens }: { itens: TreinamentoItem[] }) {
  if (itens.length === 0) return <Vazio texto="Nenhum curso disponível para esta pessoa." />;
  return (
    <div className={GRADE}>
      {itens.map((c) => (
        <CartaoItem key={c.id} titulo={c.titulo} href={`/treinamentos/curso/${c.id}`}>
          <div className="flex items-center gap-2">
            <Progress value={c.progresso} className="h-2 flex-1" />
            <span className="w-10 text-right text-xs font-medium">{c.progresso}%</span>
          </div>
          <span className="text-xs text-muted-foreground">{c.aulas_concluidas} de {c.total_aulas} aulas concluídas</span>
        </CartaoItem>
      ))}
    </div>
  );
}

/** Formatação simples por tipo de campo — tipos com arquivo (imagem/arquivo/assinatura) não
 *  baixam o anexo aqui, só sinalizam que existe. */
const formatarValorCampo = (tipo: string, valor: unknown): string => {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (['imagem', 'arquivo', 'assinatura'].includes(tipo)) return '[arquivo enviado]';
  if (Array.isArray(valor)) return valor.join(', ');
  if (tipo === 'data') {
    const d = dataCurta(typeof valor === 'string' ? valor : String(valor));
    return d ?? String(valor);
  }
  return String(valor);
};

function ListaFormularios({ itens }: { itens: FormularioItem[] }) {
  const [abertoId, setAbertoId] = useState<string | null>(null);
  if (itens.length === 0) return <Vazio texto="Nenhum formulário respondido por esta pessoa." />;
  return (
    <div className="space-y-2">
      {itens.map((f) => {
        const aberto = abertoId === f.id;
        return (
          <div key={f.id} className="rounded-lg border bg-card text-card-foreground shadow-sm">
            <button
              type="button"
              onClick={() => setAbertoId(aberto ? null : f.id)}
              className="flex w-full items-center justify-between gap-2 p-3 text-left"
            >
              <span className="min-w-0 flex-1 truncate font-medium" title={f.titulo}>{f.titulo}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{dataCurta(f.respondido_em) ?? '—'}</span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${aberto ? 'rotate-180' : ''}`} />
            </button>
            {aberto && (
              <div className="space-y-2 border-t p-3">
                {f.respostas.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sem respostas registradas.</p>
                ) : (
                  f.respostas.map((r, i) => (
                    <div key={i} className="text-xs">
                      <p className="font-medium text-muted-foreground">{r.pergunta}</p>
                      <p>{formatarValorCampo(r.tipo, r.valor)}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Itens da pessoa no módulo, cada módulo no visual da tela dele. */
export function ItensDoModulo({ userId, modulo, label, empresaId }: {
  userId: string; modulo: string; label: string; empresaId?: string;
}) {
  const [dados, setDados] = useState<unknown>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    usuarioProfileService.getItens(userId, modulo, empresaId).then((r) => {
      if (cancelado) return;
      setDados(r);
      setCarregando(false);
    });
    return () => { cancelado = true; };
  }, [userId, modulo, empresaId]);

  const total = Array.isArray(dados)
    ? dados.length
    : dados && typeof dados === 'object' && 'atividades' in dados
      ? (dados as ProjetosItens).atividades.length + (dados as ProjetosItens).projetos.length
      : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-medium">
          {label} da pessoa
          {!carregando && <Badge variant="secondary">{total}</Badge>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">Situação atual (não depende do período). Clique num item pra abrir.</p>
      </CardHeader>
      <CardContent>
        {carregando ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : dados === null ? (
          <Vazio texto="Não foi possível carregar os itens." />
        ) : modulo === 'okrs' ? (
          <ListaOkrs itens={dados as OkrItem[]} />
        ) : modulo === 'projetos' ? (
          <ListaProjetos itens={dados as ProjetosItens} />
        ) : modulo === 'organogramas' ? (
          <ListaDiagramas itens={dados as DiagramaItem[]} base="/organograma" nome="organograma" />
        ) : modulo === 'fluxogramas' ? (
          <ListaDiagramas itens={dados as DiagramaItem[]} base="/fluxogramas" nome="fluxograma" />
        ) : modulo === 'treinamentos' ? (
          <ListaTreinamentos itens={dados as TreinamentoItem[]} />
        ) : (
          <ListaFormularios itens={dados as FormularioItem[]} />
        )}
      </CardContent>
    </Card>
  );
}
